// data/obras.js - CRUD de Obras (Centro de Custo) v4.0.0
// Offline-first com Dexie/IndexedDB

import { db } from './localDb.js'
import { getEmpresaId } from './syncManager.js'
import { getSession } from '../auth/auth.js'

// Helpers
const STATUS_OBRA = {
  ativa: 'ativa',
  finalizada: 'finalizada',
  parada: 'parada'
}

export function getStatusObraLabel(status) {
  const labels = {
    [STATUS_OBRA.ativa]: '🟢 Ativa',
    [STATUS_OBRA.finalizada]: '🔵 Finalizada',
    [STATUS_OBRA.parada]: '🟡 Parada'
  }
  return labels[status] || status
}

// LISTAR OBRAS
export async function listObras({ q = '', status = 'todos' } = {}) {
  const query = q.trim().toLowerCase()
  const session = getSession()
  const empresaId = session?.empresaId
  
  // Buscar do IndexedDB
  let all = await db.obras.toArray()
  
  // Filtrar por empresa (isolation)
  if (empresaId) {
    all = all.filter(o => o.empresaId === empresaId)
  }
  
  // Aplicar filtros
  return all
    .filter((o) => {
      if (status !== 'todos' && o.status !== status) return false
      if (!query) return true
      return (
        (o.nome ?? '').toLowerCase().includes(query) ||
        (o.codigo ?? '').toLowerCase().includes(query) ||
        (o.cliente ?? '').toLowerCase().includes(query)
      )
    })
    .sort((a, b) => (a.nome ?? '').toLowerCase().localeCompare((b.nome ?? '').toLowerCase()))
}

// BUSCAR OBRA POR ID
export async function getObra(id) {
  const obra = await db.obras.get(id)
  if (!obra) return null
  
  // Verificar isolation
  const session = getSession()
  if (session?.empresaId && obra.empresaId !== session.empresaId) {
    return null // Não tem acesso
  }
  
  return obra
}

// CRIAR OBRA
export async function createObra(payload) {
  const session = getSession()
  const empresaId = session?.empresaId
  
  if (!empresaId) {
    throw new Error('Usuário não vinculado a empresa')
  }
  
  const now = Date.now()
  const obra = {
    id: crypto.randomUUID(),
    nome: (payload.nome ?? '').trim(),
    codigo: (payload.codigo ?? '').trim(),
    cliente: (payload.cliente ?? '').trim() || null,
    endereco: (payload.endereco ?? '').trim() || null,
    dataInicio: payload.dataInicio || null,
    dataPrevisaoFim: payload.dataPrevisaoFim || null,
    status: payload.status || STATUS_OBRA.ativa,
    empresaId,
    criadoEm: now,
    atualizadoEm: now,
    pendingSync: 1 // Marcar para sincronizar
  }
  
  // Salvar no IndexedDB
  await db.obras.add(obra)
  
  // Nota: sincronização com Supabase será feita automaticamente pelo syncManager
  
  return obra
}

// ATUALIZAR OBRA
export async function updateObra(id, patch) {
  const obra = await db.obras.get(id)
  if (!obra) {
    throw new Error('Obra não encontrada')
  }
  
  // Verificar isolation
  const session = getSession()
  if (session?.empresaId && obra.empresaId !== session.empresaId) {
    throw new Error('Sem permissão para editar esta obra')
  }
  
  const updates = {
    atualizadoEm: Date.now(),
    pendingSync: 1
  }
  
  if (patch.nome !== undefined) updates.nome = (patch.nome ?? '').trim()
  if (patch.codigo !== undefined) updates.codigo = (patch.codigo ?? '').trim()
  if (patch.cliente !== undefined) updates.cliente = (patch.cliente ?? '').trim() || null
  if (patch.endereco !== undefined) updates.endereco = (patch.endereco ?? '').trim() || null
  if (patch.dataInicio !== undefined) updates.dataInicio = patch.dataInicio || null
  if (patch.dataPrevisaoFim !== undefined) updates.dataPrevisaoFim = patch.dataPrevisaoFim || null
  if (patch.status !== undefined) updates.status = patch.status
  
  await db.obras.update(id, updates)
  
  // Nota: sincronização com Supabase será feita automaticamente pelo syncManager
  
  return { ...obra, ...updates }
}

// EXCLUIR OBRA (soft delete - mudar status)
export async function deleteObra(id) {
  const obra = await db.obras.get(id)
  if (!obra) {
    throw new Error('Obra não encontrada')
  }
  
  // Verificar isolation
  const session = getSession()
  if (session?.empresaId && obra.empresaId !== session.empresaId) {
    throw new Error('Sem permissão para excluir esta obra')
  }
  
  // Soft delete - marcar como finalizada
  await db.obras.update(id, {
    status: STATUS_OBRA.finalizada,
    atualizadoEm: Date.now(),
    pendingSync: 1
  })
  
  // Nota: sincronização com Supabase será feita automaticamente pelo syncManager
  
  return true
}

// OBTER OBRAS ATIVAS (para selects)
export async function getObrasAtivas() {
  return listObras({ status: STATUS_OBRA.ativa })
}

// EXPORTAR STATUS
export { STATUS_OBRA }
